import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiArrowLeft, FiSave, FiUser, FiMail, FiShield, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import axios from 'axios';
import './UpdateUser.css';
// const url = import.meta.env.VITE_BACKEND_URL;
const url = process.env.REACT_APP_BACKEND_URL;

const UpdateUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user',
    isActive: true
  });

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${url}/api/auth/${id}`);
        if (response.data.success) {
          const user = response.data.data;
          setFormData({
            name: user.name || '',
            email: user.email || '',
            role: user.role || 'user',
            isActive: user.isActive !== undefined ? user.isActive : true
          });
        } else {
          setError('Failed to fetch user data');
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError(err.response?.data?.message || 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchUser();
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put(
        `${url}/api/auth/${id}`,
        formData
      );

      if (response.data.success) {
        setSuccess('User updated successfully!');
        setTimeout(() => {
          navigate('/settings');
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="update-user-container">
        <div className="loading">Loading user data...</div>
      </div>
    );
  }

  return (
    <div className="update-user-container">
      <div className="update-user-header">
        <Link to="/settings" className="back-button">
          <FiArrowLeft /> Back to Users
        </Link>
        <h1>Edit User</h1>
        <p>Update user information and permissions</p>
      </div>

      <div className="update-user-card">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="user-form">
          <div className="form-section">
            <h3>
              <FiUser className="section-icon" />
              Basic Information
            </h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter user's full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter user's email"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>
              <FiShield className="section-icon" />
              Permissions & Status
            </h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="role">User Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="isActive">Account Status</label>
                <div className="status-toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="toggle-input"
                    />
                    <span className={`toggle-slider ${formData.isActive ? 'active' : 'inactive'}`}>
                      <span className="toggle-knob">
                        {formData.isActive ? <FiCheckCircle /> : <FiXCircle />}
                      </span>
                      <span className="toggle-text">
                        {formData.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
            >
              <FiSave />
              {saving ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>

      {/* User activity section */}
      <div className="user-activity-card">
        <h3>User Activity</h3>
        <div className="activity-info">
          <div className="activity-item">
            <span className="activity-label">Last Login:</span>
            <span className="activity-value">2024-01-15 14:30</span>
          </div>
          <div className="activity-item">
            <span className="activity-label">Last Logout:</span>
            <span className="activity-value">2024-01-15 18:45</span>
          </div>
          <div className="activity-item">
            <span className="activity-label">Account Created:</span>
            <span className="activity-value">2024-01-10</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateUser;